import { useState } from 'react';
import { useDispatch } from 'react-redux';
import { useTranslation } from 'react-i18next';
import CustomButton from '../../components/CustomButton';
import { useReactivateTenantMutation } from '../../api/account/accountApiSlice';
import { enqueueSuccessSnackbar } from '../../action/snackbarCommands';
import { errorMessageFrom } from '../../utils/errorMessage';
import { zonedFormatter } from '../../utils/stationTime';

interface ReactivateAccountSectionProps {
    slug: string;
    archivedAt: string;
    /** From the server; null only for an archive without a date (not reactivatable). */
    deletionDueAt: string | null;
}

const DATE_OPTIONS: Intl.DateTimeFormatOptions = { year: 'numeric', month: '2-digit', day: '2-digit' };

/**
 * A closed account as kolula support sees it in Master mode (#1379): when the
 * owner closed it, when its data is deleted, and — while that date is ahead —
 * "Reactivate account". An action, not a form, so it has its own button
 * rather than the "Bearbeiten" flow. The server decides the window again on
 * the click (`tenant_reactivate_retention_expired`), so a stale page cannot
 * reactivate a purged account.
 */
const ReactivateAccountSection = ({ slug, archivedAt, deletionDueAt }: ReactivateAccountSectionProps) => {
    const { t, i18n } = useTranslation();
    const dispatch = useDispatch();
    const [reactivate, { isLoading }] = useReactivateTenantMutation();
    const [error, setError] = useState<string | null>(null);

    const format = (iso: string | null) =>
        (iso ? zonedFormatter(i18n.language, DATE_OPTIONS).format(new Date(iso)) : t('common.not_applicable'));
    const windowOpen = deletionDueAt !== null && new Date(deletionDueAt).getTime() > Date.now();
    const dates = { archivedOn: format(archivedAt), deletedOn: format(deletionDueAt) };

    const handleReactivate = async () => {
        setError(null);
        try {
            await reactivate(slug).unwrap();
            dispatch(enqueueSuccessSnackbar({ message: t('account.reactivate_success'), autoHideDuration: 3000 }));
        } catch (err) {
            setError(errorMessageFrom(err, t('account.reactivate_error'), t));
        }
    };

    return (
        <section className={'bg-text-contrast rounded-[5px] shadow-sm p-6'}>
            <h2 className={'text-lg font-semibold text-foreground mb-2'}>{t('account.reactivate_title')}</h2>
            <p className={'text-sm text-foreground/60 mb-5'}>
                {windowOpen ? t('account.reactivate_description', dates) : t('account.reactivate_expired', dates)}
            </p>
            {windowOpen && (
                <CustomButton
                    title={t('account.reactivate_button')}
                    onClick={handleReactivate}
                    loading={isLoading}
                    fullWidth={false}
                />
            )}
            {error && <p className={'text-sm text-foreground mt-4'} role={'alert'}>{error}</p>}
        </section>
    );
};

export default ReactivateAccountSection;
